<template>
  <div
    v-if="isStaging"
    class="staging-banner"
    role="status"
    aria-label="Staging environment notice"
  >
    <span class="staging-banner__tag">
      <i class="bi bi-exclamation-triangle-fill"></i>
      STAGING
    </span>
    <span class="staging-banner__text">
      STAGING ENVIRONMENT — do not enter real or production data.
    </span>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { isStagingEnvironment } from '@/core/helpers/environment'

// Hostname is fixed for the lifetime of the page load, so evaluate once.
const isStaging = isStagingEnvironment()

// Reserve space for the fixed banner by flagging <body>. The class is the single switch
// for the gated offset styles below; tying it to this component's lifecycle keeps the
// whole feature self-contained and leaves production rendering untouched (fails closed).
const BODY_CLASS = 'staging-env'
onMounted(() => {
  if (isStaging) document.body.classList.add(BODY_CLASS)
})
onUnmounted(() => {
  document.body.classList.remove(BODY_CLASS)
})
</script>

<style scoped lang="scss">
.staging-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  // Above sidebar (105), toolbar (99) and route progress bar (1000); below the
  // toast container (10600) and app overlay (10500) so feedback still layers on top.
  z-index: 1090;
  height: var(--staging-banner-height, 44px);
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 20px;
  font-size: 0.85rem;
  color: #f4f6fb;
  // Dark translucent glass with a faint blur — premium, unmistakably "not production".
  background: linear-gradient(90deg, rgba(20, 22, 34, 0.97), rgba(28, 31, 48, 0.97));
  backdrop-filter: blur(6px);
  border-bottom: 1px solid rgba(255, 199, 0, 0.35);

  // Glowing amber left accent.
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 4px;
    background: var(--kt-warning, #ffc700);
    box-shadow: 0 0 14px 1px rgba(255, 199, 0, 0.65);
  }
}

.staging-banner__tag {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
  padding: 4px 11px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 1px;
  color: #ffd84d;
  background: rgba(255, 199, 0, 0.14);
  border: 1px solid rgba(255, 199, 0, 0.4);

  i {
    font-size: 0.85rem;
  }
}

.staging-banner__text {
  font-weight: 500;
  color: #d9dce6;
  // Keep the warning on a single line; truncate gracefully on very narrow viewports.
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// The dark-glass treatment already reads well in dark mode; nudge the border for contrast.
[data-theme='dark'] .staging-banner {
  border-bottom-color: rgba(255, 199, 0, 0.45);
}
</style>

<!--
  Global (un-scoped) offsets, gated behind the `staging-env` body class this component
  adds — so they are inert on production. Co-located here to keep the feature in one file.
  --staging-banner-height is declared once here as the single source of truth for the
  bar height and the space reserved for it. Padding <body> shifts in-flow content down;
  fixed top:0 elements ignore that padding, so each is nudged down explicitly above
  (sidebar, banners, progress bar, and — on tablet/mobile — the fixed header).
-->
<style lang="scss">
body.staging-env {
  --staging-banner-height: 44px;
  padding-top: var(--staging-banner-height);

  // Push the only other fixed top:0 elements below the banner so they don't overlap it.
  .session-expiry-banner,
  .app-progress-bar,
  #kt_aside {
    top: var(--staging-banner-height);
  }

  // On tablet/mobile the header is itself fixed (top:0) and carries the sidebar
  // toggle (hamburger). Without this, the banner overlays it and blocks the toggle,
  // trapping the off-canvas sidebar. Push the fixed header below the banner too.
  // Scoped to the same breakpoint as the header's fixed rule (media-breakpoint-down(lg))
  // so the desktop header — where the sidebar is in-flow and has no header toggle — is
  // left exactly as-is.
  @media (max-width: 991.98px) {
    &.header-tablet-and-mobile-fixed #kt_header {
      top: var(--staging-banner-height);
    }
  }
}
</style>
